import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { Box, Button, Container, Flex, Text, Icon, Image, } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { FaCommentDots } from "react-icons/fa";
import BackNavigation from "../components/BackNavigation";
import LatestCheckedInCoverQr from "../components/profile/LatestCheckedInCoverQr";
import { LoyaltyRewardsCard } from "../components/loyalty";
import { useCustomer } from "../contexts/CustomerProvider";
import { usePub } from "../hooks/usePub";
import { useLoyaltyWalletSummary } from "../hooks/useLoyaltyWalletSummary";
import { useRecentPubBooking } from "../hooks/useRecentPubBooking";
import { useWallet } from "../hooks/useWallet";
const parseMs = (value) => {
    if (!value)
        return 0;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
};
/**
 * Wallet API may return Jackson-serialized LocalDateTime as number arrays:
 * [year, month, day, hour, minute] or [year, month, day, hour, minute, second, nanoOfSecond].
 */
const coerceDateTimeToMs = (value) => {
    if (value == null)
        return 0;
    if (typeof value === "number" && Number.isFinite(value)) {
        if (value > 0 && value < 1e12)
            return value * 1000;
        return value;
    }
    if (typeof value === "string") {
        return parseMs(value);
    }
    if (Array.isArray(value)) {
        const parts = value.map((x) => typeof x === "number" ? x : Number(x));
        if (parts.length < 5 || parts.some((n) => !Number.isFinite(n)))
            return 0;
        const [year, month, day, hour, minute, second = 0, nano = 0] = parts;
        const msFromNano = typeof nano === "number" && Number.isFinite(nano)
            ? Math.floor(nano / 1000000)
            : 0;
        const t = new Date(year, month - 1, day, hour, minute, second, msFromNano).getTime();
        return Number.isFinite(t) ? t : 0;
    }
    return 0;
};
const isCancelledOrDenied = (status) => {
    const normalized = String(status || "").toUpperCase();
    return normalized === "CANCELLED" || normalized === "DENIED";
};
const getMetaBooking = (booking) => booking;
const getBookingStatus = (booking) => String(booking.status || getMetaBooking(booking).status || "").toUpperCase();
const normalizeBookingType = (value) => String(value || "").toUpperCase();
/** App bookings must be checked in before they can drive the account profile QR. */
const isAppBookingNotCheckedIn = (item) => normalizeBookingType(item.bookingType || String(getMetaBooking(item).booking_type || "")) === "APP" &&
    getBookingStatus(item) !== "CHECKED_IN";
const getBookingCode = (booking) => String(booking.bookingCode || getMetaBooking(booking).booking_code || "").trim();
const getEventUuid = (booking) => String(booking.eventUuid || getMetaBooking(booking).event_uuid || "").trim();
const getEventId = (booking) => {
    var _a;
    const raw = (_a = booking.eventId) !== null && _a !== void 0 ? _a : getMetaBooking(booking).event_id;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
};
const getBookingId = (booking) => {
    var _a;
    const raw = (_a = booking.bookingId) !== null && _a !== void 0 ? _a : getMetaBooking(booking).booking_id;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
};
const getSubEventObj = (booking) => {
    var _a;
    return (_a = (booking.subEvent ||
        getMetaBooking(booking).sub_event)) !== null && _a !== void 0 ? _a : null;
};
const getSubEventId = (booking) => {
    var _a;
    const metaBooking = getMetaBooking(booking);
    const fromBooking = (_a = booking.subEventId) !== null && _a !== void 0 ? _a : metaBooking.sub_event_id;
    if (fromBooking != null) {
        const parsed = Number(fromBooking);
        return Number.isFinite(parsed) ? parsed : null;
    }
    const subEvent = getSubEventObj(booking);
    if (!subEvent)
        return null;
    const fromSubEvent = subEvent.id;
    if (fromSubEvent == null)
        return null;
    const parsed = Number(fromSubEvent);
    return Number.isFinite(parsed) ? parsed : null;
};
const getCheckinTimeRaw = (booking) => { var _a; return (_a = booking.checkinTime) !== null && _a !== void 0 ? _a : getMetaBooking(booking).checkin_time; };
const getCheckinTimeMs = (booking) => coerceDateTimeToMs(getCheckinTimeRaw(booking));
const getSubEventTimeRaw = (booking, edge) => {
    var _a, _b;
    const se = getSubEventObj(booking);
    if (!se)
        return undefined;
    if (edge === "start") {
        return (_a = se.startTime) !== null && _a !== void 0 ? _a : se.start_time;
    }
    return (_b = se.endTime) !== null && _b !== void 0 ? _b : se.end_time;
};
/** Prefer sub-event window when present (including array-serialized times); else booking; else event. */
const getWindowStartMs = (booking) => {
    const subMs = coerceDateTimeToMs(getSubEventTimeRaw(booking, "start"));
    if (subMs > 0)
        return subMs;
    const bookingRaw = booking.bookingStartTime || getMetaBooking(booking).booking_start_time;
    const bMs = parseMs(String(bookingRaw !== null && bookingRaw !== void 0 ? bookingRaw : ""));
    if (bMs > 0)
        return bMs;
    const eventRaw = booking.eventStartTime || getMetaBooking(booking).event_start_time;
    return parseMs(String(eventRaw !== null && eventRaw !== void 0 ? eventRaw : ""));
};
const getWindowEndMs = (booking) => {
    const subMs = coerceDateTimeToMs(getSubEventTimeRaw(booking, "end"));
    if (subMs > 0)
        return subMs;
    const bookingRaw = booking.bookingEndTime || getMetaBooking(booking).booking_end_time;
    const bMs = parseMs(String(bookingRaw !== null && bookingRaw !== void 0 ? bookingRaw : ""));
    if (bMs > 0)
        return bMs;
    const eventRaw = booking.eventEndTime || getMetaBooking(booking).event_end_time;
    return parseMs(String(eventRaw !== null && eventRaw !== void 0 ? eventRaw : ""));
};
const getEventKey = (booking) => getEventUuid(booking) ? `uuid:${getEventUuid(booking)}` : `id:${String(getEventId(booking) || "")}`;
const getGroupKey = (booking) => {
    const eventKey = getEventKey(booking);
    const subEventId = getSubEventId(booking);
    return subEventId != null
        ? `${eventKey}:sub:${String(subEventId)}`
        : eventKey;
};
const selectPrimaryWalletBookingForCover = (walletData) => {
    const today = Array.isArray(walletData === null || walletData === void 0 ? void 0 : walletData.today_bookings)
        ? walletData.today_bookings
        : [];
    const upcoming = Array.isArray(walletData === null || walletData === void 0 ? void 0 : walletData.upcoming_bookings)
        ? walletData.upcoming_bookings
        : [];
    const merged = [...today, ...upcoming];
    const candidates = merged.filter((item) => {
        if (!getBookingCode(item))
            return false;
        if (!(getEventUuid(item) || getEventId(item) !== 0))
            return false;
        if (isCancelledOrDenied(getBookingStatus(item)))
            return false;
        if (isAppBookingNotCheckedIn(item))
            return false;
        return true;
    });
    if (!candidates.length)
        return null;
    const unique = Array.from(new Map(candidates.map((item) => [`${getEventKey(item)}:${getBookingCode(item)}`, item])).values());
    const now = Date.now();
    const classified = unique
        .map((item) => {
        const startMs = getWindowStartMs(item);
        const endMs = getWindowEndMs(item);
        const hasValidWindow = startMs > 0 && endMs > 0;
        const isOngoing = hasValidWindow && startMs <= now && now < endMs;
        const isFuture = hasValidWindow && now < startMs;
        const isUnknownWindow = !hasValidWindow;
        return {
            item,
            startMs,
            endMs,
            isOngoing,
            isFuture,
            isUnknownWindow,
        };
    })
        .filter((entry) => entry.isOngoing || entry.isFuture || entry.isUnknownWindow);
    if (!classified.length)
        return null;
    const byEvent = new Map();
    classified.forEach((entry) => {
        const eventKey = getGroupKey(entry.item);
        if (!byEvent.has(eventKey))
            byEvent.set(eventKey, []);
        byEvent.get(eventKey).push(entry);
    });
    const eventGroups = Array.from(byEvent.values()).map((entries) => {
        const ongoingEntries = entries.filter((entry) => entry.isOngoing);
        const futureEntries = entries.filter((entry) => entry.isFuture);
        const unknownEntries = entries.filter((entry) => entry.isUnknownWindow);
        const nearestStart = futureEntries.reduce((acc, entry) => (acc === 0 || entry.startMs < acc ? entry.startMs : acc), 0);
        const oldestOngoingStart = ongoingEntries.reduce((acc, entry) => (acc === 0 || entry.startMs < acc ? entry.startMs : acc), 0);
        return {
            entries,
            ongoingEntries,
            futureEntries,
            unknownEntries,
            isOngoing: ongoingEntries.length > 0,
            isFuture: ongoingEntries.length === 0 && futureEntries.length > 0,
            isUnknown: ongoingEntries.length === 0 &&
                futureEntries.length === 0 &&
                unknownEntries.length > 0,
            nearestStart,
            oldestOngoingStart,
        };
    });
    const ongoingGroups = eventGroups.filter((group) => group.isOngoing);
    const futureGroups = eventGroups.filter((group) => group.isFuture);
    const unknownGroups = eventGroups.filter((group) => group.isUnknown);
    // Hard rule: do not show ended events at all.
    const candidateGroups = ongoingGroups.length > 0
        ? ongoingGroups
        : futureGroups.length > 0
            ? futureGroups
            : unknownGroups;
    if (!candidateGroups.length)
        return null;
    const selectedGroup = [...candidateGroups].sort((a, b) => {
        var _a, _b, _c, _d;
        if (ongoingGroups.length > 0) {
            const startDelta = a.oldestOngoingStart - b.oldestOngoingStart;
            if (startDelta !== 0)
                return startDelta;
        }
        else if (futureGroups.length > 0) {
            // No ongoing event: choose nearest upcoming event.
            const startDelta = a.nearestStart - b.nearestStart;
            if (startDelta !== 0)
                return startDelta;
        }
        return (Number(((_b = (_a = a.entries[0]) === null || _a === void 0 ? void 0 : _a.item) === null || _b === void 0 ? void 0 : _b.eventId) || 0) -
            Number(((_d = (_c = b.entries[0]) === null || _c === void 0 ? void 0 : _c.item) === null || _d === void 0 ? void 0 : _d.eventId) || 0));
    })[0];
    // Within selected event, pick latest booking.
    return [...selectedGroup.entries]
        .map((entry) => entry.item)
        .sort((a, b) => {
        if (getBookingStatus(a) === "CHECKED_IN" && getBookingStatus(b) !== "CHECKED_IN")
            return -1;
        if (getBookingStatus(b) === "CHECKED_IN" && getBookingStatus(a) !== "CHECKED_IN")
            return 1;
        const checkinDelta = getCheckinTimeMs(b) - getCheckinTimeMs(a);
        if (checkinDelta !== 0)
            return checkinDelta;
        const bookingIdDelta = getBookingId(b) - getBookingId(a);
        if (bookingIdDelta !== 0)
            return bookingIdDelta;
        const bookingWindowDelta = getWindowStartMs(b) - getWindowStartMs(a);
        if (bookingWindowDelta !== 0)
            return bookingWindowDelta;
        // Final fallback when timestamps/booking id are unavailable.
        return getEventId(b) - getEventId(a);
    })[0];
};
const MenuItem = ({ label, onClick, to, href }) => {
    const content = (_jsxs(Flex, { w: "100%", py: "20px", px: 1, alignItems: "center", justifyContent: "space-between", cursor: "pointer", _hover: { opacity: 0.8 }, borderBottom: "1px solid", borderColor: "#363636", onClick: onClick, children: [_jsx(Text, { fontFamily: "Poppins", fontWeight: 500, fontSize: "14px", color: "#FFFFFF", children: label }), _jsx(Icon, { as: ChevronRightIcon, color: "#FFFFFF", w: "24px", h: "24px" })] }));
    if (href) {
        return (_jsx(Box, { as: "a", href: href, target: "_blank", rel: "noopener noreferrer", w: "100%", children: content }));
    }
    if (to) {
        return (_jsx(Box, { as: RouterLink, to: to, w: "100%", textDecoration: "none", children: content }));
    }
    return _jsx(Box, { w: "100%", children: content });
};
const ProfileMenuPage = ({ pubId, accountBasePath = "/account", }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const { customerData } = useCustomer();
    const navigate = useNavigate();
    const { data: pub } = usePub(pubId);
    const { data: walletSummary } = useLoyaltyWalletSummary(pubId ? Number(pubId) : 0);
    const { data: feedbackEligibility } = useRecentPubBooking(pubId, !!pubId && !!(customerData === null || customerData === void 0 ? void 0 : customerData.id));
    const { data: walletResponse, refetch: refetchWallet } = useWallet(pubId);
    const selectedBooking = useMemo(() => selectPrimaryWalletBookingForCover(walletResponse === null || walletResponse === void 0 ? void 0 : walletResponse.data), [walletResponse === null || walletResponse === void 0 ? void 0 : walletResponse.data]);
    const pubLogo = ((_b = (_a = pub === null || pub === void 0 ? void 0 : pub.data) === null || _a === void 0 ? void 0 : _a.pub_detail) === null || _b === void 0 ? void 0 : _b.logo) || ((_d = (_c = pub === null || pub === void 0 ? void 0 : pub.data) === null || _c === void 0 ? void 0 : _c.pub_detail) === null || _d === void 0 ? void 0 : _d.pub_logo);
    const isLoyaltyEnabled = (_f = (_e = pub === null || pub === void 0 ? void 0 : pub.data) === null || _e === void 0 ? void 0 : _e.pub_detail) === null || _f === void 0 ? void 0 : _f.isLoyaltyEnabled;
    // If not logged in, redirect to verify-phone
    useEffect(() => {
        if (!(customerData === null || customerData === void 0 ? void 0 : customerData.id)) {
            const currentPath = window.location.pathname + window.location.search;
            navigate(`/verify-phone/direct?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
        }
    }, [customerData, navigate]);
    useEffect(() => {
        if (pubId && (customerData === null || customerData === void 0 ? void 0 : customerData.id)) {
            refetchWallet();
        }
    }, [pubId, customerData === null || customerData === void 0 ? void 0 : customerData.id, refetchWallet]);
    if (!(customerData === null || customerData === void 0 ? void 0 : customerData.id))
        return null;
    const userName = (customerData === null || customerData === void 0 ? void 0 : customerData.name) || "User";
    return (_jsxs(_Fragment, { children: [_jsx(BackNavigation, { heading: "" }), _jsx(Box, { bg: "#111111", w: "100%", 
                // h="100%"
                overflow: "hidden", 
                // overscrollBehaviorY="none"
                position: "relative", children: _jsxs(Container, { w: ["100%", "100%", "100%", "400px"], minH: "100vh", background: "#111111", p: "0", overflow: "hidden", children: [_jsx(Box, { px: 6, pb: 4, pt: 24, children: _jsx(Text, { fontFamily: "Poppins", fontWeight: 500, fontSize: "24px", color: "#FFFFFF", children: userName }) }), pubId &&
                            (selectedBooking === null || selectedBooking === void 0 ? void 0 : selectedBooking.bookingCode) &&
                            !isAppBookingNotCheckedIn(selectedBooking) && (_jsx(Box, { px: 4, mb: 6, children: _jsx(LatestCheckedInCoverQr, { booking: selectedBooking }) })), pubId && (feedbackEligibility === null || feedbackEligibility === void 0 ? void 0 : feedbackEligibility.eligible) && (_jsx(Box, { px: 4, mb: 6, children: _jsxs(Box, { bg: "#181818", borderRadius: "12px", border: "1px solid", borderColor: "#363636", h: "160px", p: 5, children: [_jsxs(Flex, { alignItems: "flex-start", gap: 4, mb: 4, children: [_jsx(Flex, { w: "34px", h: "34px", flexShrink: 0, borderRadius: "79px", overflow: "hidden", alignItems: "center", justifyContent: "center", bg: pubLogo ? "transparent" : "#C4A574", children: pubLogo ? (_jsx(Image, { src: pubLogo, alt: "Venue logo", w: "34px", h: "34px", objectFit: "cover", borderRadius: "79px" })) : (_jsx(Icon, { as: FaCommentDots, color: "white", boxSize: 5 })) }), _jsxs(Box, { children: [_jsx(Text, { fontFamily: "Poppins", fontWeight: 700, fontSize: "14px", color: "#FFFFFF", mb: 1, children: "Enjoying Your Experience?" }), _jsx(Text, { fontFamily: "Glacial-Indifference", fontWeight: 400, fontSize: "12px", color: "#FFFFFF", whiteSpace: "pre-line", children: `We would love your feedback. It takes less than\na minute` })] })] }), _jsx(Box, { as: RouterLink, to: `${accountBasePath}/feedback`, display: "block", textDecoration: "none", children: _jsx(Button, { w: "100%", bg: "#FFFFFF", borderRadius: "4px", fontFamily: "Poppins", fontWeight: 400, fontSize: "14px", color: "#000000", size: "md", _hover: { bg: "gray.100" }, children: "Leave a Feedback" }) })] }) })), pubId && walletSummary && isLoyaltyEnabled && (_jsx(Box, { px: 4, mb: 6, children: _jsx(LoyaltyRewardsCard, { totalPoints: Math.floor(walletSummary.baseBalance + walletSummary.bonusBalance), tier: (((_g = walletSummary.currentTierName) === null || _g === void 0 ? void 0 : _g.toLowerCase()) ||
                                    "silver"), onClick: () => navigate("/account/loyalty") }) })), _jsxs(Box, { px: 4, pb: 8, children: [_jsx(MenuItem, { label: "Account Details", to: "/profile" }), pubId && (_jsx(MenuItem, { label: "My Visits", to: `${accountBasePath}/visits` })), pubId && (_jsx(MenuItem, { label: "Feedback History", to: `${accountBasePath}/feedback-history` }))] })] }) })] }));
};
export default ProfileMenuPage;
